import { useEffect } from 'react';
import { useLocalObservable } from 'mobx-react-lite';
import { autorun } from 'mobx';
import { AxiosResponse } from 'axios';
import { calculateUploadProgress } from '../../lib/math/calculate-upload-progress';
import { useLowerApi } from '../../../../hooks/useLowerApi/useLowerApi';
import { uuidv4 } from '../../lib/math/uuidv4';
import useSessionState from '../../../../hooks/useSessionState/useSessionState';
import { SessionStateKeys } from '../../../../models/session-state-keys';
import { FileToken } from '../../types/FileToken';
import AppConfig from '../../../../config/app-config';
import validateFile from '../../../lower-api/lib/validators/validate-file';
import { validate } from '../../../lower-fns';
import { ValidationCallback } from '../../types/ValidationCallback';
import useNotifications from '../../../../hooks/useNotifications/useNotifications';
import { NotificationType } from '../../../../hooks/useNotifications/UserNotification';

const useFileUploadModel = (onValidationError: ValidationCallback = () => {
  // Default no-op function for validation errors
}) => {
  const { lowerApi } = useLowerApi();

  const [storedTokens, setTokens] = useSessionState<FileToken[]>({
    initialState: [],
    key: SessionStateKeys.UploadTokens,
  });

  const { notify, clearNotifications } = useNotifications();

  function handleUploadErrors(err: string) {
    switch (err) {
    // Network Timeout
    case 'timeout of 60000ms exceeded':
      notify({
        message: 'Please try again with a better internet connection.',
        notificationType: NotificationType.Error,
        title: 'Your upload has timed out.',
      });
      break;
      // Default Network Error
    default:
      notify({
        message: 'Please try again with a better internet connection.',
        notificationType: NotificationType.Error,
        title: 'There was an error with the Network connection. ',
      });
      break;
    }
  }
  const store = useLocalObservable(
    () => ({
      enqueue(files: FileList) {
        const uploadPromises: Promise<void | AxiosResponse<unknown>>[] = [];

        Array.from(files).forEach(
          (file: File) => uploadPromises.push(this.upload(file)),
        );

        return Promise.all(uploadPromises);
      },
      tokens: storedTokens,
      upload(file: File) {
        // create ID
        const id = uuidv4();

        // create token
        const token: FileToken = {
          dateUploaded: new Date(),
          id,
          lastModified: file.lastModified,
          name: file.name,
          progress: 0,
          size: file.size,
          type: file.type,
        };

        // validate file
        const validationResults = validate<File>(validateFile, file);

        if (!validationResults.isValid) {
          onValidationError(token, validationResults);

          return Promise.reject();
        }
        // if valid, add to array
        this.tokens.push(token);

        // upload File
        return lowerApi.uploadFile(file, {
          onUploadProgress: (progressEvent) => {
            const index = this.tokens.findIndex((x) => x.id === id);

            if (index === -1) {
              return;
            }

            this.tokens[index].progress = calculateUploadProgress(progressEvent);
          },
        }).catch(
          (err: Error) => {
            clearNotifications();
            // if any network errors while uploading.display notification to update User..
            handleUploadErrors(err.message);
            // remove upload tokens where progress is < 100 (failed uploads)
            this.tokens = this.tokens.filter((item) => !(item.progress < 100));
          },
        );
      },
    }),
  );

  useEffect(
    () =>
      autorun(
        () => {
          if (!AppConfig.PersistUploads) return;
          const { tokens } = store;

          setTokens(tokens);
        },
      ),
    [setTokens, store],
  );

  return store;
};

export default useFileUploadModel;
